<template>
  <!-- Concepts -->
  <tr v-if="item.is === 'concept'" v-on="$listeners" v-bind="$attrs">
    <td v-if="!hideLevelType" class="top-align" :class="'pl-'.concat(new Number(item.level) * 3 + 5)">
      <div>{{ (item.level === undefined ? ' ' : item.level) + "-" + item.type }}</div>
    </td>
    <td class="top-align text-no-wrap" :class="getConceptTypeStyling(item)">
      <div>{{ item.code }}</div>
    </td>
    <td v-if="itemsInValueSetHasOrdinal">
      <div v-if="item.ordinal" :class="$filters.getConceptTypeStyling(item.type)">{{item.ordinal}}</div>
    </td>
    <td class="top-align" :class="getConceptTypeStyling(item)">
      <div>
        {{ item.displayName }}
        <div v-if="item.desc" class="text--disabled">
          <span v-html="$filters.getMatchingTranslation({ value: item.desc })"></span>
        </div>
        <template v-if="!hideDesignations">
          <!-- CAVE: for expansion sets the returned designation type is in property "use" not "type" as anywhere else-->
          <div
            v-for="(designation, i) in $filters.getMatchingDesignations({ value: item.designation })"
            :key="designation.type + i"
          >
            <span>{{ designation.displayName }}</span>
            <!-- CAVE: for expansion sets the returned language is in property "lang" not "language" as anywhere else -->
            <v-chip v-if="designation.type" small class="ml-2 my-1" style="cursor: inherit; pointer-events: none;">
              {{ getDesignationTypeTranslation(designation.type) }}
              <flag
                v-if="designation.language"
                :squared="false"
                :iso="designation.language.substr(3, 5)"
                class="ml-2"
              />
            </v-chip>
            <span v-if="showAction && item.designation.length > 1 && i === 0" class="ml-1"> +{{ item.designation.length - 1 }}</span>
          </div>
        </template>
      </div>
    </td>
    <td class="top-align" :class="getConceptTypeStyling(item)">
      <div>
        <span>{{ sourceCodeSystemName ?? getCodeSystemName(item.codeSystem) }}</span>
      </div>
    </td>
    <slot name="extraCells"/>
    <td v-if="showAction" align="right">
      <AdSpeedDial
        v-model="item.speeddial"
        :open-on-hover="false"
        direction="left"
        transition="slide-y-reverse-transition"
        style="position: relative;"
      >
        <AdSpeedOption
          v-if="!actionsToHide.includes('edit')"
          style="position:relative;z-index:9;"
          type="edit" :hint="$t('edit-item')"
          @click="handleClick('editConceptItemStart', item)"
        />
        <v-menu v-if="!actionsToHide.includes('add')" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <AdSpeedOption
              type="add" :hint="$t('add-item')"
              v-bind="attrs"
              v-on="on"
              @click.stop.prevent=""
            />
          </template>
          <v-list dense>
            <template v-for="(insertPosition, key) of insertPositions">
              <v-list-item
                @click="onInsert({...insertPosition, item})"
                :key="key"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ $t(insertPosition.text) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
        <AdSpeedOption
          v-if="!actionsToHide.includes('delete')"
          style="position:relative;z-index:9;"
          type="delete" :hint="$t('delete-item')"
          @click="handleClick('removeConceptItem', item)"
        />
      </AdSpeedDial>
    </td>
  </tr>
  <!-- Include or exclude -->
  <tr v-else-if="['include', 'exclude'].includes(item.is)" v-on="$listeners" v-bind="$attrs">
    <td v-if="!hideLevelType">
      <span v-if="item.type">{{ item.type }}</span>
      <span v-else>&ndash;</span>
    </td>
    <td class="top-align nowrap">
      <span style="white-space: nowrap;">
        <v-icon color="grey">
          {{ item.is === 'include' ? 'mdi-plus-circle' : 'mdi-minus-circle' }}
        </v-icon>
        {{ $t(item.is) }}
      </span>
    </td>
    <td v-if="itemsInValueSetHasOrdinal">
      <div v-if="item.ordinal" :class="$filters.getConceptTypeStyling(item.type)">{{item.ordinal}}</div>
    </td>
    <td class="top-align">
        <template v-if="item.ref">
          <span>
            <!-- this is an include of a value set -->
            {{ $t('value-set') }}
            <span class="font-italic">{{ getValueSetNameByIdAndVersion(item.ref, item.flexibility)}}</span>
            ({{item.ref}})
          </span>
          <span v-if="item.flexibility && item.flexibility !== 'dynamic'" class="text-no-wrap">
            {{$t('as-of')}}
            {{ $filters.formatDateShort(item.flexibility)}}
          </span>
          <span v-else>
            {{ $t('dynamic') }}
          </span>
        </template>
        <template v-else-if="item.op">
          <!-- this is an include of a concept with operator -->
          {{ item.op }}
          {{ $t('code') }}
          {{ item.code }}
          <span class="font-italic">
            {{ item.displayName }}
          </span>
        </template>
        <template v-else-if="item.codeSystem && !item.code">
          <div :class="$filters.getConceptTypeStyling(item.type)">
            <span>
              <!-- this is an include of a complete code system -->
              {{ $t('codesystem') }}
              <span class="font-italic">{{ getCodeSystemName(item.codeSystem)}}</span>
              ({{item.codeSystem}})
            </span>
            <span v-if="item.flexibility && item.flexibility !== 'dynamic'" class="text-no-wrap">
              {{$t('as-of')}}
              {{ $filters.formatDateShort(item.flexibility)}}
            </span>
            <span v-else>
              {{ $t('dynamic') }}
            </span>
          </div>
        </template>
        <template v-else-if="item.codeSystem && item.code">
          <!-- this is an exclude of a single concept -->
          {{ $t('code') }}
          {{ item.code }}
          <span class="font-italic">
            {{ item.displayName }}
          </span>
        </template>
        <template v-if="item.filter">
          <div class="text--disabled">
            <!-- check include complete code system with filters -->
            <span v-if="Array.isArray(item.filter) && item.filter.length">{{ $t('filter-on-code-property') }}</span>
            <template v-for="(filter, key) of item.filter">
              <ul :key="key" class="my-1">
                <li>
                <span class="font-weight-bold mr-2">{{ filter.property }}</span>
                <span class="font-italic mr-2">{{ filter.op }}</span>
                <span>{{ filter.value }}</span>
                </li>
              </ul>
            </template>
          </div>
        </template>
        <template v-else>
          <!-- this is an include of an unknown intensional construct -->
        </template>
        <span v-if="item.exception === 'true'">{{ $t('as-exception') }}</span>
    </td>
    <td>
      {{ getCodeSystemName(item.codeSystem) }}
    </td>
    <slot name="extraCells"/>
    <td v-if="showAction" align="right">
      <AdSpeedDial
        v-model="item.speeddial"
        :open-on-hover="false"
        direction="left"
        transition="slide-y-reverse-transition"
        style="position: relative;"
      >
        <AdSpeedOption
          v-if="!actionsToHide.includes('edit')"
          style="position:relative;z-index:9;"
          type="edit" :hint="$t('edit-item')"
          @click="handleClick('editConceptItemStart', item)"
        />
        <v-menu v-if="!actionsToHide.includes('add')" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <AdSpeedOption
              type="add" :hint="$t('add-item')"
              v-bind="attrs"
              v-on="on"
              @click.stop.prevent=""
            />
          </template>
          <v-list dense>
            <template v-for="(insertPosition, key) of insertPositions">
              <v-list-item
                @click="onInsert({...insertPosition, item})"
                :key="key"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ $t(insertPosition.text) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
        <AdSpeedOption
          v-if="!actionsToHide.includes('delete')"
          style="position:relative;z-index:9;"
          type="delete" :hint="$t('delete-item')"
          @click="handleClick('removeConceptItem', item)"
        />
      </AdSpeedDial>
    </td>
  </tr>
  <!-- Complete code systems -->
  <tr v-else-if="item.is === 'completeCodeSystem'" v-on="$listeners" v-bind="$attrs">
    <!-- skip column level and code -->
    <td v-if="!hideLevelType">&ndash;</td>
    <td/>
    <td v-if="itemsInValueSetHasOrdinal">
      <div v-if="item.ordinal" :class="$filters.getConceptTypeStyling(item.type)">{{item.ordinal}}</div>
    </td>
    <td>
      {{ getCodeSystemName(item.codeSystem) }}
      <div class="text--disabled">
        {{ $t('complete-codesystem') }}
        <!-- check include complete code system with filters -->
        <span v-if="item.filter"> – {{ $t('filter-on-code-property') }}</span>
        <template v-for="(filter, key) of item.filter">
          <!--
          <div :key="key" class="my-2">
            <v-chip label color="green lighten-3">
              <v-icon left color="green darken-3">
                mdi-filter
              </v-icon>
              {{ filter.property }}
            </v-chip>
            <v-chip color="red lighten-4">{{ filter.op }} </v-chip>
            <v-chip label color="grey lighten-2">{{ filter.value}}</v-chip>
          </div>
          -->
          <ul :key="key" class="my-1">
            <li>
            <span class="font-weight-bold mr-2">{{ filter.property }}</span>
            <span class="font-italic mr-2">{{ filter.op }}</span>
            <span>{{ filter.value }}</span>
            </li>
          </ul>
        </template>
      </div>
    </td>
    <td>{{ item.codeSystem }}</td>
    <slot name="extraCells"/>
    <td v-if="showAction" align="right">
      <AdSpeedDial
        v-model="item.speeddial"
        :open-on-hover="false"
        direction="left"
        transition="slide-y-reverse-transition"
        style="position: relative;"
      >
        <AdSpeedOption
          v-if="!actionsToHide.includes('edit')"
          style="position:relative;z-index:9;"
          type="edit" :hint="$t('edit-item')"
          @click="handleClick('editConceptItemStart', item)"
        />
        <v-menu v-if="!actionsToHide.includes('add')" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <AdSpeedOption
              type="add" :hint="$t('add-item')"
              v-bind="attrs"
              v-on="on"
              @click.stop.prevent=""
            />
          </template>
          <v-list dense>
            <template v-for="(insertPosition, key) of insertPositions">
              <v-list-item
                @click="onInsert({...insertPosition, item})"
                :key="key"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ $t(insertPosition.text) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
        <AdSpeedOption
          v-if="!actionsToHide.includes('delete')"
          style="position:relative;z-index:9;"
          type="delete" :hint="$t('delete-item')"
          @click="handleClick('removeConceptItem', item)"
        />
      </AdSpeedDial>
    </td>
  </tr>
  <!-- Exceptions -->
  <tr v-else-if="item.is === 'exception'" v-on="$listeners" v-bind="$attrs">
    <td v-if="!hideLevelType" class="top-align" :class="'pl-'.concat(new Number(item.level) * 3 + 5)">
      {{ item.level + "-" + item.type }}
    </td>
    <td class="top-align nowrap" :class="getConceptTypeStyling(item)">
      {{ item.code }}
    </td>
    <td v-if="itemsInValueSetHasOrdinal">
      <div v-if="item.ordinal" :class="$filters.getConceptTypeStyling(item.type)">{{item.ordinal}}</div>
    </td>
    <td class="top-align" :class="getConceptTypeStyling(item)">
      {{ item.displayName }}
      <div v-if="item.desc" class="text--disabled">
        <span v-html="$filters.getMatchingTranslation({ value: item.desc })"></span>
      </div>
    </td>
    <td class="top-align" :class="getConceptTypeStyling(item)">
      <span v-if="sourceCodeSystems.length > 0">{{ getCodeSystemName(item.codeSystem) }}</span>
      <span v-else>{{ item.codeSystem }}</span>
    </td>
    <slot name="extraCells"/>
    <td v-if="showAction" align="right">
      <AdSpeedDial
        v-model="item.speeddial"
        :open-on-hover="false"
        direction="left"
        transition="slide-y-reverse-transition"
        style="position: relative;"
      >
        <AdSpeedOption
          v-if="!actionsToHide.includes('edit')"
          style="position:relative;z-index:9;"
          type="edit" :hint="$t('edit-item')"
          @click="handleClick('editConceptItemStart', item)"
        />
        <v-menu v-if="!actionsToHide.includes('add')" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <AdSpeedOption
              type="add" :hint="$t('add-item')"
              v-bind="attrs"
              v-on="on"
              @click.stop.prevent=""
            />
          </template>
          <v-list dense>
            <template v-for="(insertPosition, key) of insertPositions">
              <v-list-item
                @click="onInsert({...insertPosition, item})"
                :key="key"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ $t(insertPosition.text) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
        <AdSpeedOption
          v-if="!actionsToHide.includes('delete')"
          style="position:relative;z-index:9;"
          type="delete" :hint="$t('delete-item')"
          @click="handleClick('removeConceptItem', item)"
        />
      </AdSpeedDial>
    </td>
  </tr>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'TerminologyValueSetConceptItem',
  props: {
    itemsInValueSetHasOrdinal: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      required: true
    },
    hideDesignations: {
      type: Boolean,
      default: false
    },
    showEditable: {
      type: Boolean,
      default: true
    },
    hideLevelType: {
      type: Boolean,
      default: false
    },
    // Specify actions to hide from the SpeedDial menu.
    actionsToHide: {
      type: Array,
      default: () => [],
      required: false,
      validator: v => Array.isArray(v) && v.every(action => ['delete', 'edit', 'add'].includes(action))
    },
    sourceCodeSystemName: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      insertPositions: [{
        type: 'item',
        mode: 'preceding',
        text: 'insert-new-item-before'
      }, {
        type: 'item',
        mode: 'following',
        text: 'insert-new-item-after'
      }]
    }
  },
  computed: {
    ...mapGetters({
      valueSetItem: ['terminology/valueSetItem'],
      valueSetItemIsEditable: ['terminology/valueSetItemIsEditable'],
      valueSetItems: ['terminology/getValueSet'],
      valueSetVersionIndex: ['terminology/valueSetVersionIndex'],
      projectInFocus: ['project/getProjectInFocus'],
      getCodeSystemName: ['terminology/getCodeSystemName'],
      getDecorTypes: ['server/getDecorTypes']
    }),
    getConceptTypeStyling () {
      return item => this.$filters.getConceptTypeStyling(item.type)
    },
    showAction () {
      return (
        this.showEditable && (
          this.valueSetItemIsEditable ||
        (
          this.$store.state.terminology.shoppingCart &&
          this.$store.state.terminology.shoppingCart.length
        ) ||
          this.$store.getters['terminology/cartWithAssociations']?.length
        )
      )
    },
    getValueSetNameByIdAndVersion () {
      return (id, flexibility) => {
        const valueSet = this.valueSetItems.find(vs => vs.id === id)
        if (!flexibility || flexibility === 'dynamic') {
          return valueSet?.displayName || ''
        }
        return valueSet?.valueSet.find(vs => vs.id === id && vs.effectiveDate === flexibility)?.displayName || ''
      }
    },
    sourceCodeSystems () {
      return this.valueSetItem?.sourceCodeSystem || []
    },
    decorTypes () {
      return this.getDecorTypes?.data || {}
    },
    designationType () {
      return this.decorTypes?.DesignationType?.[0]?.enumeration?.map((e) => {
        const text = this.$filters.getMatchingTranslation({ value: e.label, locale: this.$i18n.locale })
        return {
          '#text': text.charAt(0).toUpperCase() + text.slice(1),
          value: e.value
        }
      })
    },
    getDesignationTypeTranslation () {
      return type => this.designationType.find(d => d.value === type)?.['#text'] || type
    }
  },
  methods: {
    handleClick (action, item) {
      this.$emit('handleClick', action, item)
    },
    onInsert (position) {
      this.$emit('handleClick', 'addConceptToCompositionStart', position)
    }

  }
}
</script>
